import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { FormEvent, useState } from "react";
import {
  FaClock,
  FaEnvelope,
  FaHeadset,
  FaMessage,
  FaPaperPlane,
  FaPhone,
  FaTicket,
} from "react-icons/fa6";

const subjectOptions = [
  { value: "support", label: "Suporte de campanha" },
  { value: "payment", label: "Pagamentos e tickets" },
  { value: "reward", label: "Prêmios e entregas" },
  { value: "partnership", label: "Parcerias" },
  { value: "other", label: "Outros assuntos" },
];

const contactChannels = [
  {
    title: "Atendimento",
    description: "Tire dúvidas sobre tickets, campanhas e pagamentos.",
    icon: <FaHeadset className="text-white fs-4" />,
    action: "Abrir atendimento",
  },
  {
    title: "E-mail",
    description: "Envie sua mensagem para análise da equipe.",
    icon: <FaEnvelope className="text-white fs-4" />,
    action: "Enviar e-mail",
  },
  {
    title: "Campanhas",
    description: "Fale sobre uma campanha, compra ou prêmio recebido.",
    icon: <FaTicket className="text-white fs-4" />,
    action: "Informar campanha",
  },
];

export function ContactUs() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: subjectOptions[0].value,
    message: "",
  });

  function showSoon() {
    pushToastify({
      message: "Em breve.",
      type: "info",
      theme: "light",
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    showSoon();
  }

  return (
    <section className="bg-light pt-5 pb-4 px-3 overflow-hidden">
      <div className="container px-3 px-md-4 px-lg-0">
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4">
          <div>
            <span className="badge bg-warning text-white mb-2 fs-6">
              Atendimento ao participante
            </span>
            <h1 className="h3 fw-bold mb-1">Fale Conosco</h1>
            <p className="text-muted mb-0">
              Envie sua dúvida, solicitação ou sugestão para nossa equipe.
            </p>
          </div>

          <div className="d-flex align-items-start align-items-lg-center gap-2 text-muted">
            <FaClock className="text-warning mt-1 mt-lg-0" />
            <span className="small">
              Central em estruturação. As ações serão liberadas em breve.
            </span>
          </div>
        </div>

        <div className="row g-3 g-lg-4">
          <div className="col-12 col-lg-7">
            <Card className="border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom">
                <h2 className="h5 fw-bold mb-1">Enviar mensagem</h2>
                <p className="text-muted small mb-0">
                  Preencha os dados abaixo para deixar sua solicitação pronta.
                </p>
              </div>

              <form className="card-body" onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <Input
                      animated
                      label="Nome"
                      placeholder="Seu nome"
                      value={form.name}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <Input
                      animated
                      label="E-mail"
                      placeholder="seu@email.com"
                      type="email"
                      value={form.email}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          email: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <Input
                      animated
                      label="Telefone"
                      placeholder="(00) 00000-0000"
                      mask="phone"
                      value={form.phone}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          phone: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-floating">
                      <select
                        className="form-select"
                        id="contact-subject"
                        value={form.subject}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            subject: event.target.value,
                          }))
                        }
                      >
                        {subjectOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="contact-subject">Assunto</label>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-floating">
                      <textarea
                        className="form-control"
                        id="contact-message"
                        placeholder="Descreva sua solicitação"
                        style={{ minHeight: 140 }}
                        value={form.message}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            message: event.target.value,
                          }))
                        }
                      />
                      <label htmlFor="contact-message">Mensagem</label>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={showSoon}
                  >
                    <FaMessage className="me-2" />
                    Abrir chat
                  </button>

                  <button type="submit" className="btn btn-warning text-white">
                    <FaPaperPlane className="me-2" />
                    Enviar mensagem
                  </button>
                </div>
              </form>
            </Card>
          </div>

          <div className="col-12 col-lg-5">
            <div className="d-flex flex-column gap-3 h-100">
              {contactChannels.map((channel) => (
                <Card className="border-0 shadow-sm" key={channel.title}>
                  <div className="card-body d-flex gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0 bg-warning bg-opacity-10 text-warning"
                      style={{ width: 48, height: 48, borderRadius: 8 }}
                    >
                      {channel.icon}
                    </div>

                    <div className="flex-grow-1">
                      <h3 className="h6 fw-bold mb-1">{channel.title}</h3>
                      <p className="text-muted small mb-3">
                        {channel.description}
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning"
                        onClick={showSoon}
                      >
                        {channel.action}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}

              <Card className="border-0 shadow-sm bg-dark text-white">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FaPhone className="text-warning" />
                    <h3 className="h6 fw-bold mb-0">Canais oficiais</h3>
                  </div>
                  <p className="small mb-0 text-white-50">
                    Em breve você poderá acessar telefone, WhatsApp e histórico
                    de atendimento direto por aqui.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
